import React from 'react';
import DefaultScreen from '@components/Global/DefaultScreenView';
import apis from '@api/api';
import { useQuery } from '@tanstack/react-query';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import { FlatList, View, Platform, ScrollView, Pressable } from 'react-native';
import { SizableText, XStack, YStack } from 'tamagui';
import { FontAwesome5, AntDesign } from '@expo/vector-icons';
import { WhatsAppTemplateCard } from './TemplatesScreen/components/WhatsAppTemplateCard';
import { Typography, Paper, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import LottieView from 'lottie-react-native';
import CountUp from 'react-countup';
import ErrorBoundary from '@utils/ErrorBoundary';
import { LinearGradient } from 'expo-linear-gradient';

// Styled components for modern theming
const ModernContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 20,
  padding: 24,
  marginVertical: 16,
  marginHorizontal: Platform.OS === 'web' ? '5%' : 10,
  backgroundColor: theme?.palette?.mode === 'dark' ? '#2d2d2d' : '#ffffff',
  boxShadow: theme?.palette?.mode === 'dark'
    ? '0 8px 32px rgba(0,0,0,0.4)'
    : '0 8px 32px rgba(0,0,0,0.12)',
  border: theme?.palette?.mode === 'dark'
    ? '1px solid rgba(255,255,255,0.1)'
    : '1px solid rgba(0,0,0,0.05)',
  overflow: 'hidden',
  position: 'relative',
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontSize: '1.5rem',
  fontWeight: 700,
  color: theme?.palette?.mode === 'dark' ? '#ffffff' : '#333333',
  marginBottom: 16,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  borderBottomWidth: 1,
  borderBottomColor: theme?.palette?.mode === 'dark'
    ? 'rgba(255,255,255,0.1)'
    : 'rgba(0,0,0,0.08)',
  paddingBottom: 12,
}));

const LoadingContainer = styled(View)(({ theme }) => ({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  paddingVertical: 60,
  minHeight: 200,
}));

const getStatsContainerStyle = (theme: boolean) => ({
  backgroundColor: theme ? 'rgba(102, 126, 234, 0.1)' : 'rgba(74, 144, 226, 0.1)',
  borderRadius: 12,
  padding: 16,
  marginVertical: 8,
});

const AnimatedSection = styled(Animatable.View)(({ theme }) => ({
  flex: 1,
  marginVertical: 8,
}));

const WhatsappTemplates = () => {
  const [myTemplatesOpen, setMyTemplatesOpen] = React.useState(false);
  const [templatesOpen, setTemplatesOpen] = React.useState(false);
  const theme = themeSelector();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['whatsappTemplates'],
    queryFn: async () => {
      try {
        const result = await apis.getWhatsappTemplates();
        return result || { data: [] };
      } catch (apiError) {
        console.error('❌ WhatsApp templates API error:', apiError);
        throw apiError;
      }
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 2, // 2 minutes
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const onRefresh = React.useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <DefaultScreen
      containerStyle={{ zIndex: -1 }}
      style={{ paddingRight: 0 }}
      backButton
      searchBar={{
        customIcons: [{
          function: () => {
            console.log("opening WhatsApp template");
            // Add navigation to create template
          },
          functionName: 'createWhatsAppTemplate',
          icon: <AntDesign name="file-add" size={16} color={"white"} />
        }]
      }}
    >
      <ErrorBoundary>
        <ScrollView style={{ zIndex: -1, flex: 1, padding: 20, flexDirection: 'column', maxWidth: '110%' }}>
          {
            isLoading ?
              <LoadingContainer>
                {Platform.OS === 'web' ? (
                  <div style={{ width: 60, height: 60, marginBottom: 16 }}>
                    <div className="spinner-border text-primary" role="status">
                    </div>
                  </div>
                ) : (
                  <LottieView
                    source={require('@assets/animations/loading.json')}
                    autoPlay
                    loop
                    style={{ width: 60, height: 60, marginBottom: 16 }}
                  />
                )}
                <Typography style={{
                  color: theme ? '#666666' : '#cccccc',
                  fontSize: 16,
                  fontWeight: 500,
                  textAlign: 'center'
                }}>
                  Loading WhatsApp templates...
                </Typography>
              </LoadingContainer>
              :
              <AnimatedSection
                animation="fadeInUp"
                duration={800}
                style={{ marginBottom: 20 }}
              >
                {/* Background gradient overlay */}
                <LinearGradient
                  colors={theme
                    ? ['rgba(102, 126, 234, 0.05)', 'rgba(118, 75, 162, 0.05)']
                    : ['rgba(74, 144, 226, 0.08)', 'rgba(53, 122, 189, 0.08)']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: 20,
                    zIndex: -1,
                  }}
                />

                <ModernContainer
                  elevation={theme ? 6 : 10}
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    marginBottom: 2

                  }}
                >

                  {templatesOpen && (
                    <Animatable.View animation="fadeInDown" duration={600}>
                      <View style={{ ...getStatsContainerStyle(theme), flex: 1, minWidth: '45%' }}>
                        <View style={{ flexDirection: "row", alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography style={{
                            color: theme ? '#333333' : '#ffffff',
                            fontWeight: 600,
                            fontSize: 16
                          }}>
                            Active Templates
                          </Typography>
                          <Tooltip title="Add new WhatsApp template" arrow>
                            <Pressable
                              style={{
                                backgroundColor: theme ? '#667eea' : '#4a90e2',
                                borderRadius: 20,
                                padding: 8,
                              }}
                            >
                              <AntDesign name="pluscircle" size={16} color="white" />
                            </Pressable>
                          </Tooltip>
                        </View>
                      </View>
                    </Animatable.View>
                  )}
                </ModernContainer>
              </AnimatedSection>
          }

          <AnimatedSection
            animation="fadeInUp"
            duration={800}
            delay={200}
            style={{ flex: 1, marginBottom: 20 }}
          >
            {/* Company Templates Background gradient */}
            <LinearGradient
              colors={theme
                ? ['rgba(118, 75, 162, 0.05)', 'rgba(102, 126, 234, 0.05)']
                : ['rgba(53, 122, 189, 0.08)', 'rgba(74, 144, 226, 0.08)']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 20,
                zIndex: -1,
              }}
            />

            <ModernContainer
              elevation={theme ? 6 : 10}
              sx={{ position: 'relative', zIndex: 1, flex: 1 }}
            >
              {/* Company Templates Header */}
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 20
              }}>
                <View
                  style={{
                    backgroundColor: theme ? 'rgba(102, 126, 234, 0.1)' : 'rgba(74, 144, 226, 0.1)',
                    borderRadius: 10,
                    padding: 10,
                    marginRight: 12
                  }}
                >
                  <FontAwesome5 name="whatsapp" size={18} color={theme ? '#667eea' : '#4a90e2'} />
                </View>
                <SectionHeader variant="h6" style={{ fontSize: '1.25rem', marginBottom: 0, borderBottom: 'none', paddingBottom: 0 }}>
                  Active WhatsApp Templates
                </SectionHeader>
              </View>

              <FlatList
                data={data?.data || []}
                centerContent
                ListEmptyComponent={(
                  <View style={{ ...getStatsContainerStyle(theme), marginVertical: 16 }}>
                    <View style={{ alignItems: 'center', padding: 20 }}>
                      <Animatable.View
                        animation="pulse"
                        iterationCount="infinite"
                        duration={2000}
                        style={{ marginBottom: 16 }}
                      >
                        <FontAwesome5 name="whatsapp" size={40} color={theme ? '#667eea' : '#4a90e2'} />
                      </Animatable.View>
                      <Typography style={{
                        lineHeight: 1.6,
                        color: theme ? '#333333' : '#ffffff',
                        fontSize: 16,
                        textAlign: 'center',
                        marginBottom: 8
                      }}>
                        Create WhatsApp Templates
                      </Typography>
                      <Typography style={{
                        lineHeight: 1.6,
                        color: theme ? '#666666' : '#cccccc',
                        fontSize: 14,
                        textAlign: 'center'
                      }}>
                        Add templates that will be available throughout your company so members can make customised templates
                      </Typography>
                    </View>
                  </View>
                )}
                renderItem={({ item, index }) => (
                  <View style={{ minHeight: 400 }}>
                    <WhatsAppTemplateCard item={item} />
                  </View>
                )}
                horizontal={!!data?.data?.length}
                contentContainerStyle={{ gap: 20, paddingRight: 20, paddingBottom: 20 }}
                showsHorizontalScrollIndicator={false}
                style={{ flex: 1, minHeight: 350 }}
              />
            </ModernContainer>
          </AnimatedSection>

          {/* Suggested WhatsApp Templates Section */}
          <AnimatedSection
            animation="fadeInUp"
            duration={800}
            delay={400}
            style={{ marginBottom: 20 }}
          >
            {/* Suggested Templates Background gradient */}
            <LinearGradient
              colors={theme
                ? ['rgba(102, 126, 234, 0.05)', 'rgba(118, 75, 162, 0.05)']
                : ['rgba(74, 144, 226, 0.08)', 'rgba(102, 126, 234, 0.08)']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: 20,
                zIndex: -1,
              }}
            />

            <ModernContainer
              elevation={theme ? 6 : 10}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              {/* Suggested Templates Header */}
              <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                marginBottom: 20
              }}>
                <Animatable.View
                  animation="pulse"
                  iterationCount="infinite"
                  duration={2000}
                  style={{
                    backgroundColor: theme ? 'rgba(102, 126, 234, 0.1)' : 'rgba(74, 144, 226, 0.1)',
                    borderRadius: 10,
                    padding: 10,
                    marginRight: 12
                  }}
                >
                  <FontAwesome5 name="robot" size={18} color={theme ? '#667eea' : '#4a90e2'} />
                </Animatable.View>
                <View style={{ flex: 1 }}>
                  <SectionHeader variant="h6" style={{ fontSize: '1.25rem', marginBottom: 4, borderBottom: 'none', paddingBottom: 0 }}>
                    Suggested WhatsApp Templates
                  </SectionHeader>
                  <Typography style={{
                    color: theme ? '#667eea' : '#4a90e2',
                    fontSize: 14,
                    fontWeight: 600
                  }}>
                    AI-Powered Template Recommendations
                  </Typography>
                </View>
                <Tooltip title="Generate AI templates" arrow>
                  <Pressable
                    style={{
                      backgroundColor: theme ? 'rgba(102, 126, 234, 0.1)' : 'rgba(74, 144, 226, 0.1)',
                      borderRadius: 8,
                      padding: 8,
                      marginLeft: 12
                    }}
                  >
                    <AntDesign name="thunderbolt" size={18} color={theme ? '#667eea' : '#4a90e2'} />
                  </Pressable>
                </Tooltip>
              </View>

              {/* AI Readiness Badge */}
              <View style={{ ...getStatsContainerStyle(theme), marginBottom: 16 }}>
                <XStack alignItems="center" justifyContent="space-between">
                  <XStack alignItems="center" space="$3">
                    <View
                      style={{
                        backgroundColor: theme ? '#667eea' : '#4a90e2',
                        borderRadius: 20,
                        padding: 8,
                        marginRight: 8
                      }}
                    >
                      <AntDesign name="check-circle" size={16} color="white" />
                    </View>
                    <YStack>
                      <Typography style={{
                        color: theme ? '#333333' : '#ffffff',
                        fontWeight: 700,
                        fontSize: 16
                      }}>
                        AI Ready Templates
                      </Typography>
                      <Typography style={{
                        color: theme ? '#666666' : '#cccccc',
                        fontSize: 12
                      }}>
                        Templates optimized for AI-powered messaging
                      </Typography>
                    </YStack>
                  </XStack>
                  <View style={{
                    backgroundColor: theme ? 'rgba(16, 185, 129, 0.1)' : 'rgba(5, 150, 105, 0.1)',
                    borderRadius: 12,
                    paddingHorizontal: 12,
                    paddingVertical: 6
                  }}>
                    <Typography style={{
                      color: theme ? '#10b981' : '#059669',
                      fontSize: 12,
                      fontWeight: 600
                    }}>
                      READY
                    </Typography>
                  </View>
                </XStack>
              </View>

              {/* Suggested Templates List */}
              <FlatList
                data={[
                  {
                    name: "Welcome Message",
                    category: "UTILITY",
                    language: "EN",
                    status: "SUGGESTED",
                    components: [
                      { type: "HEADER", text: "Welcome to {{company_name}}!" },
                      { type: "BODY", text: "Hi {{first_name}}, welcome to our platform! We're excited to help you succeed. 🚀" },
                      { type: "FOOTER", text: "Reply STOP to unsubscribe" }
                    ]
                  },
                  {
                    name: "Appointment Reminder",
                    category: "UTILITY",
                    language: "EN",
                    status: "SUGGESTED",
                    components: [
                      { type: "HEADER", text: "Appointment Reminder" },
                      { type: "BODY", text: "Hi {{first_name}}, this is a reminder about your appointment on {{date}} at {{time}}. Please confirm your attendance." },
                      { type: "FOOTER", text: "Reply CONFIRM to confirm" }
                    ]
                  },
                  {
                    name: "Order Confirmation",
                    category: "UTILITY",
                    language: "EN",
                    status: "SUGGESTED",
                    components: [
                      { type: "HEADER", text: "Order Confirmed!" },
                      { type: "BODY", text: "Hi {{first_name}}, your order #{{order_number}} has been confirmed. Expected delivery: {{delivery_date}}" },
                      { type: "FOOTER", text: "Track your order at {{tracking_link}}" }
                    ]
                  }
                ]}
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 16, paddingRight: 20 }}
                ListEmptyComponent={(
                  <View style={{ ...getStatsContainerStyle(theme), marginVertical: 16, minWidth: 300 }}>
                    <View style={{ alignItems: 'center', padding: 20 }}>
                      <Animatable.View
                        animation="pulse"
                        iterationCount="infinite"
                        duration={2000}
                        style={{ marginBottom: 16 }}
                      >
                        <FontAwesome5 name="robot" size={40} color={theme ? '#667eea' : '#4a90e2'} />
                      </Animatable.View>
                      <Typography style={{
                        lineHeight: 1.6,
                        color: theme ? '#333333' : '#ffffff',
                        fontSize: 16,
                        textAlign: 'center',
                        marginBottom: 8
                      }}>
                        Generate AI Templates
                      </Typography>
                      <Typography style={{
                        lineHeight: 1.6,
                        color: theme ? '#666666' : '#cccccc',
                        fontSize: 14,
                        textAlign: 'center',
                        marginBottom: 16
                      }}>
                        Let AI create personalized templates for your business needs
                      </Typography>
                      <Pressable
                        style={{
                          backgroundColor: theme ? '#667eea' : '#4a90e2',
                          borderRadius: 20,
                          paddingHorizontal: 20,
                          paddingVertical: 10
                        }}
                      >
                        <Typography style={{
                          color: 'white',
                          fontSize: 14,
                          fontWeight: 600
                        }}>
                          Generate Templates
                        </Typography>
                      </Pressable>
                    </View>
                  </View>
                )}
                renderItem={({ item, index }) => (
                  <View style={{ minHeight: 400, width: 320 }}>
                    <WhatsAppTemplateCard item={item} />
                  </View>
                )}
              />
            </ModernContainer>
          </AnimatedSection>
        </ScrollView>
      </ErrorBoundary>
    </DefaultScreen>
  )
}
export default WhatsappTemplates;